import { currencyConfigPerRegion, formatNumber } from '@lemonade-hq/lemonation';
import type { I18nContextValue } from '@lemonade-hq/nuiza';
import type { CustomFunctions, EXTENSION_FNS, JSONObject, JSONValue } from '@lemonade-hq/shozer';
import {
  registerFunctions,
  shozer,
  TYPE_ARRAY,
  TYPE_BOOLEAN,
  TYPE_NULL,
  TYPE_NUMBER,
  TYPE_OBJECT,
  TYPE_STRING,
} from '@lemonade-hq/shozer';

import type { DateComponents } from '@lemonade-hq/ts-helpers';
import deepmerge from 'deepmerge';
import { format } from 'libphonenumber-js';
import type { NumberFormat } from 'libphonenumber-js';
import type { InterpolableTextProps } from '../components/interpolable_text';
import type { StringifyUnion } from './types';

type InterpolationStart = '{';
type InterpolationEnd = '}';
type PipeSeparator = '|';
type FunctionStart = '(';
type FunctionEnd = ')';

type Operation = 'diff' | 'divide' | 'multiply' | 'sum';
type OperandValue = number | string | null;

export type InterpolateContext = Omit<I18nContextValue, 'extendTranslations'>;
// eslint-disable-next-line functional/prefer-readonly-type
type InterpolateContextRef = { current: InterpolateContext };

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createPipes = (contextRef: InterpolateContextRef) =>
  ({
    boolean: [
      ([value, trueText, falseText]: [boolean | null, string | null, string | null]): string =>
        value != null
          ? value
            ? trueText ?? contextRef.current.s('chat.interpolate.boolean.true')
            : falseText ?? contextRef.current.s('chat.interpolate.boolean.false')
          : '',
      [
        { types: [TYPE_NULL, TYPE_BOOLEAN] },
        { types: [TYPE_NULL, TYPE_STRING], optional: true },
        { types: [TYPE_NULL, TYPE_STRING], optional: true },
      ],
    ],
    listify: [
      ([value]: [unknown]): string => {
        const list =
          value == null
            ? []
            : Array.isArray(value)
              ? value
              : typeof value === 'object'
                ? Object.values(value as Record<string, unknown>)
                : [value];

        // in e2e tests on webkit `Intl.ListFormat` is not available. A ticket was opened with playwright, but until then - this is a temp fix
        // https://github.com/microsoft/playwright/issues/23978
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return Intl.ListFormat != null
          ? new Intl.ListFormat(contextRef.current.locale, { style: 'long', type: 'conjunction' }).format(list)
          : /* istanbul ignore next */
            list.join(', ');
      },
      [{ types: [TYPE_ARRAY, TYPE_OBJECT, TYPE_NULL, TYPE_NUMBER, TYPE_STRING] }],
    ],
    date: [
      ([value, type]: [
        Date | DateComponents | number | string | null,
        Intl.DateTimeFormatOptions['dateStyle'],
      ]): string => {
        if (value == null) return '';

        let date;

        if (value instanceof Date || typeof value === 'string' || typeof value === 'number') {
          date = new Date(value);
        } else {
          date = new Date(Date.UTC(value.year, value.month - 1, value.day));
        }

        return date.toLocaleDateString(contextRef.current.locale, { dateStyle: type, timeZone: 'UTC' });
      },
      [{ types: [TYPE_NULL, TYPE_STRING, TYPE_NUMBER, TYPE_OBJECT] }, { types: [TYPE_STRING], optional: true }],
    ],
    currency: [
      ([value, currency = currencyConfigPerRegion[contextRef.current.region].currency]: [
        number | null,
        string,
      ]): string =>
        value != null
          ? Intl.NumberFormat(contextRef.current.locale, {
              style: 'currency',
              currency,
              minimumFractionDigits: 0,
            }).format(value)
          : '',
      [{ types: [TYPE_NULL, TYPE_NUMBER] }, { types: [TYPE_STRING], optional: true }],
    ],
    phone: [
      ([value, numberFormat = 'NATIONAL']: [string | null, NumberFormat]): string => {
        if (value == null) return '';

        return format(value, numberFormat.toUpperCase() as NumberFormat);
      },
      [{ types: [TYPE_NULL, TYPE_STRING] }, { types: [TYPE_STRING], optional: true }],
    ],
    number: [
      ([value]: [number | null]): string =>
        value != null ? formatNumber(value, { locale: contextRef.current.locale, minimumFractionDigits: 0 }) : '',
      [{ types: [TYPE_NULL, TYPE_NUMBER] }],
    ],
    replace: [
      ([value, original, replacement]: [string | null, string, string]): string => {
        if (value == null) return '';

        const searchValue =
          original.startsWith('/') && original.endsWith('/') ? new RegExp(original.slice(1, -1), 'g') : original;
        return value.replaceAll(searchValue, replacement);
      },
      [{ types: [TYPE_NULL, TYPE_STRING] }, { types: [TYPE_STRING] }, { types: [TYPE_STRING] }],
    ],
    calc: [
      ([operation, inputValue, secondValue]: [Operation, OperandValue, OperandValue]): number | string => {
        if (inputValue == null || secondValue == null) return '';

        const firstOperand = typeof inputValue === 'string' ? Number(inputValue) : inputValue;
        const secondOperand = typeof secondValue === 'string' ? Number(secondValue) : secondValue;

        if (isNaN(firstOperand) || isNaN(secondOperand)) {
          return '';
        }

        switch (operation.toLowerCase()) {
          case 'sum':
            return firstOperand + secondOperand;
          case 'diff':
            return firstOperand - secondOperand;
          case 'multiply':
            return firstOperand * secondOperand;
          case 'divide':
            if (secondOperand === 0) return '';

            return firstOperand / secondOperand;
          case 'round':
            return Math.round(firstOperand / secondOperand) * secondOperand;
          case 'floor':
            return Math.floor(firstOperand / secondOperand) * secondOperand;
          default:
            return `Error: Unknown operation type "${operation}". Supported types: "sum", "diff", "multiply", "divide", "round", "floor"`;
        }
      },
      [
        { types: [TYPE_STRING] },
        { types: [TYPE_NUMBER, TYPE_STRING, TYPE_NULL] },
        { types: [TYPE_NUMBER, TYPE_STRING, TYPE_NULL] },
      ],
    ],
  }) as const satisfies CustomFunctions;

export type Pipe = `${
  | keyof ReturnType<typeof createPipes>
  | keyof typeof EXTENSION_FNS}${FunctionStart}${string}${FunctionEnd}`;

type InterpolationValidator<
  TAttributes extends string,
  TStr extends string,
> = TStr extends `${infer Start} ${PipeSeparator} ${infer End}`
  ? Start extends Pipe | TAttributes
    ? End extends Pipe
      ? `${Start} ${PipeSeparator} ${End}`
      : End extends `${infer Rest}${FunctionStart}${string}${FunctionEnd} ${PipeSeparator} ${string}`
        ? `${Rest}${FunctionStart}${string}${FunctionEnd}` extends Pipe
          ? `${Start} ${PipeSeparator} ${InterpolationValidator<TAttributes, End>}`
          : never
        : never
    : never
  : TStr extends Pipe | TAttributes
    ? TStr
    : never;

type Interpolated<
  TAttributes extends string,
  TStr extends string,
> = TStr extends `${infer Start}${InterpolationStart}${infer Inter}${InterpolationEnd}${infer Rest}`
  ? `${Start}${InterpolationStart}${InterpolationValidator<TAttributes, Inter>}${InterpolationEnd}${Interpolated<
      TAttributes,
      Rest
    >}`
  : TStr;

export type InterpolationString<TAttributes extends string, TStr extends string> =
  Interpolated<NoInfer<TAttributes>, TStr> extends never
    ? `incorrect usage of attributes '${StringifyUnion<NoInfer<TAttributes>>}' or pipes '${StringifyUnion<Pipe>}'`
    : Interpolated<NoInfer<TAttributes>, TStr>;

const DEFAULT_S = (): string => '';
const DEFAULT_CONTEXT: InterpolateContext = {
  region: 'US',
  lang: 'en',
  locale: 'en-US',
  s: DEFAULT_S,
  t: DEFAULT_S,
  activeLocales: ['en-US'],
  activeRegions: ['US'],
};
const pipesContextRef: InterpolateContextRef = { current: DEFAULT_CONTEXT };
let registered = false;
let previousContext: Partial<InterpolateContext> | undefined;

interface InterpolateOptions {
  readonly context?: Partial<InterpolateContext>;
  readonly encode?: boolean;
}

export const interpolate = <
  TReturn extends JSONValue | string = string,
  const TAttributes extends string = string,
  TStr extends string = string,
>(
  str: InterpolationString<NoInfer<TAttributes>, TStr>,
  data: Record<TAttributes, unknown> | Record<TAttributes, unknown>[],
  options: InterpolateOptions = {},
): NoInfer<TReturn> => {
  const { context, encode } = options;

  if (context != null && previousContext !== context) {
    pipesContextRef.current = deepmerge(pipesContextRef.current, { ...DEFAULT_CONTEXT, ...context });
    previousContext = context;
  }

  if (!registered) {
    registerFunctions(createPipes(pipesContextRef));
    registered = true;
  }

  return shozer(str, data as JSONObject, encode ? { wrap: expr => `encodeUrl(${expr})` } : undefined) as TReturn;
};

// util for tests, resetting the context so tests are not affected by previous tests
export const resetPipesContextRef = (): void => {
  pipesContextRef.current = DEFAULT_CONTEXT;
  previousContext = undefined;
};

export const isInterpolableText = <T>(value: T): value is InterpolableTextProps & T => {
  if (typeof value === 'string') return true;

  const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
  const hasTextKey = isObject && 'text' in value;
  const hasActionsOrTooltips = isObject && ('actions' in value || 'tooltips' in value);
  if (hasTextKey && hasActionsOrTooltips) {
    return true;
  }

  return false;
};
